import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/auth/AuthContext';
import { computeStage } from '@/lib/workflow';
import { useT } from '@/i18n/LanguageContext';
import { STATUS_VALUES, getCurrentCycle, cycleOf, getFormStartDate, formTypeLabel } from './history/historyUtils';
import { cycleBounds } from '@/lib/leaveTransferWeeks';
import { HistoryFilters } from './history/HistoryFilters';
import { BulkActionBar } from './history/BulkActionBar';
import { HistoryTable } from './history/HistoryTable';
import { HistoryPivot } from './history/HistoryPivot';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SubmissionsHistory() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useT();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterDept, setFilterDept] = useState('all');
  const [filterMonth, setFilterMonth] = useState(getCurrentCycle); // "YYYY-MM" or 'all' — defaults to current cycle
  const [filterStatus, setFilterStatus] = useState(() => {
    if (typeof window !== 'undefined') {
      const s = new URLSearchParams(window.location.search).get('status');
      if (s && STATUS_VALUES.includes(s)) return s;
    }
    return 'all';
  }); // 'all' | 'awaiting_sign' | 'awaiting_check' | 'awaiting_approve' | 'approved'
  const [selectedIds, setSelectedIds] = useState([]);
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'pivot'
  const [pivotRow, setPivotRow] = useState('employee'); // 'employee' | 'department' | 'month'
  const [sortKey, setSortKey] = useState('date'); // 'date' | 'type' | 'name' | 'department' | 'printed' | 'print_check'
  const [sortDir, setSortDir] = useState('desc'); // 'asc' | 'desc'
  const [stagesByType, setStagesByType] = useState({}); // form_type → stages[]
  const [wfEnabled, setWfEnabled] = useState(true);
  const [approverDeptNames, setApproverDeptNames] = useState(() => new Set());

  const fetchSubmissions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/forms/submissions`);
      setSubmissions(response.data);
    } catch (error) {
      console.error('Failed to fetch submissions:', error);
      toast.error(t('hist.t.fetchFail'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchSubmissions(); }, [fetchSubmissions]);

  useEffect(() => {
    axios.get(`${API}/workflow-config`)
      .then((r) => {
        setWfEnabled(r.data?.enabled !== false);
        const map = {};
        (r.data?.forms || []).forEach((f) => { if (f.stages?.length) map[f.form_type] = f.stages; });
        setStagesByType(map);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    axios.get(`${API}/departments`)
      .then((r) => {
        const names = new Set();
        (r.data || []).forEach((d) => {
          if ((d.approvers || []).some((a) => a.user_id === user.id)) names.add(d.name);
        });
        setApproverDeptNames(names);
      })
      .catch(() => {});
  }, [user?.id]);

  const handleDelete = async (id) => {
    if (!window.confirm(t('hist.confirmDelete'))) return;
    try {
      await axios.delete(`${API}/forms/submissions/${id}`);
      toast.success(t('hist.t.deleted'));
      setSelectedIds((prev) => prev.filter((s) => s !== id));
      fetchSubmissions();
    } catch (error) {
      console.error('Failed to delete:', error);
      toast.error(t('hist.t.deleteFail'));
    }
  };

  const handleResend = async (id) => {
    try {
      await axios.post(`${API}/forms/submissions/${id}/resend`);
      toast.success(t('hist.t.resent'));
    } catch (error) {
      console.error('Failed to resend:', error);
      toast.error(t('hist.t.resendFail'));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredSubmissions.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredSubmissions.map((s) => s.id));
    }
  };

  const handleBulkPrint = () => {
    if (selectedIds.length === 0) {
      toast.error(t('hist.t.selectForPrint'));
      return;
    }
    navigate(`/print?ids=${selectedIds.join(',')}`);
  };

  const handleBulkPdf = () => {
    if (selectedIds.length === 0) {
      toast.error(t('hist.t.selectForPdf'));
      return;
    }
    navigate(`/print?ids=${selectedIds.join(',')}&mode=pdf`);
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(t('hist.confirmBulkDelete', { n: selectedIds.length }))) return;
    try {
      await axios.post(`${API}/forms/submissions/bulk-delete`, { ids: selectedIds });
      toast.success(t('hist.t.bulkDeleted', { n: selectedIds.length }));
      setSelectedIds([]);
      const res = await axios.get(`${API}/forms/submissions`);
      setSubmissions(res.data);
    } catch (e) {
      toast.error(t('hist.t.bulkDeleteFail'));
    }
  };

  const handleBulkResend = async () => {
    if (selectedIds.length === 0) return;
    try {
      const res = await axios.post(`${API}/forms/submissions/bulk-resend`, { ids: selectedIds });
      toast.success(t('hist.t.bulkResent', { n: res.data.count }));
    } catch (e) {
      toast.error(t('hist.t.bulkResendFail'));
    }
  };

  const handleBulkExportCsv = () => {
    if (selectedIds.length === 0) return;
    const selected = submissions.filter((s) => selectedIds.includes(s.id));
    const headers = ['ID', t('hist.csv.type'), t('hist.csv.name'), t('hist.csv.dept'), t('hist.csv.date'), t('hist.csv.submitted'), t('hist.csv.printed'), t('hist.csv.data')];
    const rows = selected.map((s) => [
      s.id,
      formTypeLabel(t, s.form_type),
      s.employee_name,
      s.department || '',
      getFormStartDate(s) || '',
      s.created_at || '',
      s.last_printed_at || '',
      JSON.stringify(s.form_data || {}),
    ]);
    const escape = (v) => {
      const str = String(v ?? '');
      return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };
    const csv = '\uFEFF' + [headers, ...rows].map((row) => row.map(escape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const now = new Date();
    const stamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
    a.href = url;
    a.download = `submissions_${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(t('hist.t.csvDone', { n: selected.length }));
  };

  const handleBulkExportXlsx = async () => {
    if (selectedIds.length === 0) return;
    try {
      const res = await axios.post(
        `${API}/forms/submissions/export-xlsx`,
        { ids: selectedIds },
        { responseType: 'blob' },
      );
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const now = new Date();
      const stamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;
      a.href = url;
      a.download = `submissions_${stamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t('hist.t.xlsxDone', { n: selectedIds.length }));
    } catch (e) {
      toast.error(t('hist.t.xlsxFail'));
    }
  };

  const handleBulkMarkPrinted = async () => {
    if (selectedIds.length === 0) return;
    try {
      await axios.post(`${API}/forms/submissions/mark-printed`, { ids: selectedIds });
      toast.success(t('hist.t.markedPrinted', { n: selectedIds.length }));
      const res = await axios.get(`${API}/forms/submissions`);
      setSubmissions(res.data);
    } catch (e) {
      toast.error(t('hist.t.markPrintedFail'));
    }
  };

  // ── 印刷確認 (manual checkbox — department approvers only) ──
  const canPrintCheck = (sub) =>
    user?.role === 'admin' || approverDeptNames.has(sub.department);

  const handlePrintCheck = async (sub, checked) => {
    try {
      const res = await axios.post(`${API}/forms/submissions/${sub.id}/print-check`, { checked });
      setSubmissions((prev) => prev.map((s) => (s.id === sub.id ? res.data : s)));
      toast.success(checked ? t('hist.t.printChecked') : t('hist.t.printUnchecked'));
    } catch (e) {
      toast.error(e.response?.data?.detail || t('hist.t.printCheckFail'));
    }
  };

  const filteredSubmissions = submissions.filter((sub) => {
    const matchesSearch = sub.employee_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || sub.form_type === filterType;
    const matchesDept = filterDept === 'all' || sub.department === filterDept;
    let matchesMonth = true;
    if (filterMonth !== 'all') {
      // 締め（前月16日→当月15日）。境界の計算は印刷の週まとめと同じ1か所を使う。
      const { start, end: endDay } = cycleBounds(filterMonth);
      const end = new Date(endDay.getFullYear(), endDay.getMonth(), endDay.getDate(), 23, 59, 59, 999);
      const refStr = getFormStartDate(sub) || sub.created_at;
      const ref = refStr ? new Date(refStr) : null;
      matchesMonth = !!(ref && !isNaN(ref.getTime()) && ref >= start && ref <= end);
    }
    const matchesStatus =
      !wfEnabled ||
      filterStatus === 'all' ||
      computeStage(sub, stagesByType[sub.form_type]) === filterStatus;
    return matchesSearch && matchesType && matchesDept && matchesMonth && matchesStatus;
  });

  // ── Sortable columns ────────────────────────────────────────────────
  const sortValue = (sub) => {
    switch (sortKey) {
      case 'date':       return getFormStartDate(sub) || sub.created_at || '';
      case 'type':       return formTypeLabel(t, sub.form_type);
      case 'name':       return (sub.employee_name || '').toLowerCase();
      case 'department': return sub.department || '';
      case 'printed':    return sub.last_printed_at || '';
      case 'print_check': return sub.print_check?.checked ? (sub.print_check.at || 'z') : '';
      default:           return '';
    }
  };
  const sortedSubmissions = [...filteredSubmissions].sort((a, b) => {
    const av = sortValue(a);
    const bv = sortValue(b);
    if (av === bv) return 0;
    // Empty values bubble to the bottom regardless of direction
    if (!av) return 1;
    if (!bv) return -1;
    const cmp = av > bv ? 1 : -1;
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      // Dates default to descending (newest first); text default ascending.
      setSortDir(key === 'date' || key === 'printed' || key === 'print_check' ? 'desc' : 'asc');
    }
  };

  // Build a list of available 部署 / months from current data
  const departmentOptions = Array.from(new Set(submissions.map((s) => s.department).filter(Boolean))).sort();
  const monthOptions = (() => {
    const set = new Set();
    submissions.forEach((s) => {
      const refStr = getFormStartDate(s) || s.created_at;
      if (!refStr) return;
      const d = new Date(refStr);
      if (isNaN(d.getTime())) return;
      set.add(cycleOf(d));
    });
    // Always include the current cycle + any active selection so the dropdown
    // can display them even when there's no data yet.
    set.add(getCurrentCycle());
    if (filterMonth !== 'all') set.add(filterMonth);
    return Array.from(set).sort().reverse();
  })();

  // Click a pivot cell → apply filters & jump to list view
  const drillDown = (rowKey, formTypeValue) => {
    if (pivotRow === 'department') setFilterDept(rowKey);
    else if (pivotRow === 'month') {
      // recover YYYY-MM from the label "YYYY年MM月 (..)" / "YYYY-MM (..)"
      const m = String(rowKey).match(/(\d{4})年(\d{1,2})月/) || String(rowKey).match(/^(\d{4})-(\d{1,2})/);
      if (m) setFilterMonth(`${m[1]}-${String(m[2]).padStart(2, '0')}`);
    } else if (pivotRow === 'employee') {
      setSearchTerm(rowKey);
    }
    if (formTypeValue) setFilterType(formTypeValue);
    setViewMode('list');
  };

  const allSelected = filteredSubmissions.length > 0 && selectedIds.length === filteredSubmissions.length;

  const hasActiveFilters =
    searchTerm !== '' || filterType !== 'all' || filterDept !== 'all' || filterMonth !== 'all' || filterStatus !== 'all';

  const resetFilters = () => {
    setSearchTerm('');
    setFilterType('all');
    setFilterDept('all');
    setFilterMonth('all');
    setFilterStatus('all');
  };

  return (
    <div className="page-enter">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-zinc-900 mb-2 sm:mb-3">
          {t('hist.title')}
        </h1>
        <p className="text-xs sm:text-sm md:text-base text-zinc-600 leading-relaxed">
          {t('hist.subtitle')}
        </p>
      </div>

      <HistoryFilters
        searchTerm={searchTerm} setSearchTerm={setSearchTerm}
        filterType={filterType} setFilterType={setFilterType}
        filterDept={filterDept} setFilterDept={setFilterDept}
        filterMonth={filterMonth} setFilterMonth={setFilterMonth}
        filterStatus={filterStatus} setFilterStatus={setFilterStatus}
        wfEnabled={wfEnabled}
        departmentOptions={departmentOptions}
        monthOptions={monthOptions}
        hasActiveFilters={hasActiveFilters}
        resetFilters={resetFilters}
        filteredCount={filteredSubmissions.length}
        totalCount={submissions.length}
        viewMode={viewMode} setViewMode={setViewMode}
      />

      {selectedIds.length > 0 && (
        <BulkActionBar
          count={selectedIds.length}
          showResend={filteredSubmissions.some((s) => selectedIds.includes(s.id) && s.form_type === 'leave')}
          onPrint={handleBulkPrint}
          onPdf={handleBulkPdf}
          onResend={handleBulkResend}
          onMarkPrinted={handleBulkMarkPrinted}
          onCsv={handleBulkExportCsv}
          onXlsx={handleBulkExportXlsx}
          onDelete={handleBulkDelete}
          onClear={() => setSelectedIds([])}
        />
      )}

      {loading ? (
        <div className="bg-white border border-zinc-200 rounded-md p-12 text-center">
          <p className="text-zinc-500">{t('hist.loading')}</p>
        </div>
      ) : viewMode === 'pivot' ? (
        <HistoryPivot
          submissions={filteredSubmissions}
          pivotRow={pivotRow}
          setPivotRow={setPivotRow}
          onDrillDown={drillDown}
          filterType={filterType}
          filterDept={filterDept}
          filterMonth={filterMonth}
        />
      ) : filteredSubmissions.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-md p-12 text-center">
          <p className="text-zinc-500 mb-2">{t('hist.empty')}</p>
          <p className="text-xs text-zinc-400">{t('hist.emptyHint')}</p>
        </div>
      ) : (
        <HistoryTable
          submissions={sortedSubmissions}
          selectedIds={selectedIds}
          allSelected={allSelected}
          onToggleSelect={toggleSelect}
          onToggleSelectAll={toggleSelectAll}
          sortKey={sortKey}
          sortDir={sortDir}
          onToggleSort={toggleSort}
          wfEnabled={wfEnabled}
          stagesByType={stagesByType}
          canPrintCheck={canPrintCheck}
          onPrintCheck={handlePrintCheck}
          onDelete={handleDelete}
          onResend={handleResend}
        />
      )}
    </div>
  );
}
